import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { api, type S } from "@/shared/api/client";
import { Field } from "@/shared/ui/Field";
import { LogoPicker, saveCompany } from "@/modules/settings";

// Countries whose companies may charge VAT (Kuwait and Qatar have none).
const VAT_COUNTRIES = ["EG", "SA", "AE", "BH", "OM"];

/** What the welcome step asks about the company: none of it has to be answered then. */
export interface CompanyAnswers {
  vat?: boolean;
  tax_number: string;
  commercial_register: string;
  city: string;
  line1: string;
}

export const noCompanyAnswers: CompanyAnswers = { tax_number: "", commercial_register: "", city: "", line1: "" };

/** Saves what was answered: VAT registration (with its number), then the registration and address. */
export async function saveCompanyAnswers(c: S["Company"], a: CompanyAnswers) {
  if (a.vat !== undefined) await api.put(`/v1/core/companies/${c.id}/vat`, { registered: a.vat, tax_number: a.vat ? a.tax_number.trim() : undefined });
  if (a.commercial_register.trim() || a.city.trim() || a.line1.trim()) {
    await saveCompany(c.id, {
      commercial_register: a.commercial_register.trim() || c.commercial_register,
      address: { ...c.address, city: a.city.trim() || c.address?.city, line1: a.line1.trim() || c.address?.line1 },
    });
  }
}

/** The details the company's documents carry: logo, VAT registration and number, registration and address. */
export function CompanyDetails({ company: c, value: a, onChange }: { company: S["Company"]; value: CompanyAnswers; onChange: (a: CompanyAnswers) => void }) {
  const { t } = useTranslation();
  const set = (k: "tax_number" | "commercial_register" | "city" | "line1") => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...a, [k]: e.target.value });
  return (
    <div className="panel space-y-5 p-5">
      {VAT_COUNTRIES.includes(c.country) && (
        <div>
          <span id="welcome-vat" className="label">{t("welcome.company.vatQuestion")}</span>
          <div role="radiogroup" aria-labelledby="welcome-vat" className="flex flex-wrap gap-2">
            {[true, false].map((yes) => (
              <button key={String(yes)} type="button" role="radio" aria-checked={a.vat === yes} onClick={() => onChange({ ...a, vat: yes })}
                className={clsx("panel px-4 py-2 font-medium transition-colors", a.vat === yes ? "border-ledger bg-ledger-soft/60" : "hover:border-ink-3")}>
                {t(yes ? "welcome.company.vatYes" : "welcome.company.vatNo")}
              </button>
            ))}
          </div>
          {a.vat === true && (
            <Field label={t("welcome.company.taxNumber")} hint={t("welcome.company.taxNumberHint")} className="mt-3 max-w-sm" required>
              <input className="field num" dir="ltr" value={a.tax_number} onChange={set("tax_number")} />
            </Field>
          )}
          {a.vat === false && <p className="mt-2 text-[13px] text-ink-2">{t("welcome.company.noVAT")}</p>}
        </div>
      )}
      <div>
        <span className="label">{t("settings.logo")}</span>
        <LogoPicker companyId={c.id} fileId={c.logo_file_id} />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label={t("welcome.company.cr")}>
          <input className="field num" dir="ltr" value={a.commercial_register} placeholder={c.commercial_register || undefined} onChange={set("commercial_register")} />
        </Field>
        <Field label={t("welcome.company.city")}>
          <input className="field" value={a.city} placeholder={c.address?.city || undefined} onChange={set("city")} />
        </Field>
        <Field label={t("welcome.company.street")}>
          <input className="field" value={a.line1} placeholder={c.address?.line1 || undefined} onChange={set("line1")} />
        </Field>
      </div>
    </div>
  );
}
